import { TrendingDown, TrendingUp } from "lucide-react"

import type { TratamientoDiferencia } from "@/componentes/ordenes/tipos"
import { comoMonto } from "@/componentes/tesoreria/dinero"

/**
 * El pago no coincide con lo pendiente de la orden: cuánto, en qué sentido, y qué se
 * hace con la diferencia.
 *
 * ⚠ **La diferencia no se decide sola.** OP-2026-0004 se pagó por 19.757,82 Bs y la
 * orden decía 19.883,36: el sistema asentó la cifra de la orden y los 125,54 Bs se
 * perdieron sin que nadie lo decidiera. «Lo pagado es ley», pero qué significa la
 * diferencia —el proveedor aceptó menos, falta pagarle el resto, o le quedó un
 * crédito— sólo lo sabe quien paga. Sin elegir, no se ejecuta.
 *
 * ⚠ **El saldo queda de UNO de los dos lados, y se nombra como lo dice tesorería.**
 * Pagando de menos, el saldo es **a favor del proveedor**: la agencia le debe el resto.
 * Pagando de más, es **saldo disponible de la empresa** frente a él: él le debe a la
 * agencia. Por eso hay dos opciones en cada sentido y no tres: la otra obligaría al
 * servidor a rechazarla con un 409.
 */
export function DiferenciaPago({
  diferencia,
  moneda,
  valor,
  onCambiar,
}: {
  /** `lo que cubre el pago − lo pendiente`, en céntimos de la moneda de la orden. */
  diferencia: bigint
  moneda: string
  valor: TratamientoDiferencia | null
  onCambiar: (valor: TratamientoDiferencia) => void
}) {
  if (diferencia === 0n) return null
  const deMenos = diferencia < 0n
  const cifra = `${comoMonto(deMenos ? -diferencia : diferencia)} ${moneda}`
  const opciones: { valor: TratamientoDiferencia; titulo: string; detalle: string }[] = deMenos
    ? [
        {
          valor: "SALDO_PROVEEDOR",
          titulo: `Dejar ${cifra} como saldo a favor del proveedor`,
          detalle:
            "La agencia se los debe: la orden sigue en «Pagos por ejecutar» hasta pagar el resto.",
        },
        {
          valor: "CERRAR",
          titulo: "Cerrar la orden con lo pagado",
          detalle: "El proveedor aceptó menos: no se le debe nada más.",
        },
      ]
    : [
        {
          valor: "SALDO_EMPRESA",
          titulo: `Dejar ${cifra} como saldo disponible de la empresa`,
          detalle: "El proveedor los debe: se descuentan al pagarle otra orden suya.",
        },
        {
          valor: "CERRAR",
          titulo: "Cerrar la orden: el excedente es gasto",
          detalle: "No se le reclama ni se le descuenta después.",
        },
      ]
  const Icono = deMenos ? TrendingDown : TrendingUp

  return (
    <fieldset
      className="space-y-2 rounded-[var(--radius-tarjeta)] border p-3"
      style={{ borderColor: "var(--warning)" }}
    >
      <legend className="flex items-center gap-2 px-1 text-sm font-semibold">
        <Icono size={16} aria-hidden={true} style={{ color: "var(--warning)" }} />
        Estás pagando <span className="tabular-nums">{cifra}</span>{" "}
        {deMenos ? "de menos" : "de más"}
      </legend>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        ¿Qué se hace con la diferencia?
      </p>
      {opciones.map((o) => (
        <label key={o.valor} className="flex cursor-pointer items-start gap-2 text-sm">
          <input
            type="radio"
            name="tratamiento-diferencia"
            className="mt-1"
            checked={valor === o.valor}
            onChange={() => onCambiar(o.valor)}
          />
          <span>
            <span className="font-medium">{o.titulo}</span>
            <span className="block text-xs" style={{ color: "var(--fg-muted)" }}>
              {o.detalle}
            </span>
          </span>
        </label>
      ))}
    </fieldset>
  )
}
